import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Phone, MapPin, Loader2, CheckCircle2, AlertTriangle, User, Clock4 } from "lucide-react";
import { PRICING_CATEGORIES } from "../data/pricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "+420 775 659 129";

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  service_category: PRICING_CATEGORIES[0].label,
  service: "",
  vehicle: "",
  message: "",
  gdpr: false,
};

const Contact = React.forwardRef(function Contact({ preset, onConsumePreset }, ref) {
  const formRef = useRef(null);
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!preset) return;
    const category = preset.category;
    const service = preset.service;
    const t = setTimeout(() => {
      setForm((f) => ({ ...f, service_category: category, service }));
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      onConsumePreset?.();
    }, 0);
    return () => clearTimeout(t);
  }, [preset, onConsumePreset]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.name.trim() || !form.phone.trim() || !form.service_category) {
      setErrorMsg("Vyplňte prosím jméno, telefon a kategorii služby.");
      setStatus("error");
      return;
    }
    if (!form.gdpr) {
      setErrorMsg("Pro odeslání prosím potvrďte souhlas se zpracováním osobních údajů.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service_category: form.service_category,
        service: form.service.trim() || null,
        vehicle: form.vehicle.trim() || null,
        message: form.message.trim() || null,
      };
      Object.keys(payload).forEach(
        (k) => (payload[k] === null || payload[k] === "") && delete payload[k]
      );
      await axios.post(`${API}/orders`, payload);
      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      setErrorMsg(
        err?.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : "Odeslání se nezdařilo. Zkontrolujte prosím vyplněné údaje."
          : "Odeslání se nezdařilo. Zkuste to prosím znovu nebo zavolejte."
      );
      setStatus("error");
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      ref={ref}
      className="relative bg-[#0A0D17] text-white py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 hero-grid opacity-30" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.22em] text-amber-300 font-semibold">
            Kontakt
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Kontaktujte nás
          </h2>
          <p className="font-body mt-5 text-lg text-slate-300">
            Stačí krátký formulář, nebo zavoláte přímo Lence.
          </p>
        </div>

        {/* Contact info tiles */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <InfoTile icon={<User className="h-5 w-5" />} label="Lenka Garguláková" sub="Vaše kontaktní osoba" />
          <InfoTile
            icon={<Phone className="h-5 w-5" />}
            label={PHONE}
            sub="Zavolejte, rádi Vám poradíme"
            href={`tel:${PHONE.replace(/\s+/g, "")}`}
            testid="contact-phone-link"
            accent
          />
          <InfoTile
            icon={<MapPin className="h-5 w-5" />}
            label="Sedlešovice 34"
            sub="Nový Šaldorf — autoopravna"
          />
          <InfoTile
            icon={<Clock4 className="h-5 w-5" />}
            label="info.stkkomfort@gmail.com"
            sub="E-mail – odpovídáme do hodiny"
            href="mailto:info.stkkomfort@gmail.com"
            testid="contact-email-link"
          />
        </div>

        {/* Form */}
        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold leading-tight">
              Objednejte se.{" "}
              <span className="bg-gradient-to-b from-amber-200 to-amber-500 bg-clip-text text-transparent">
                Zbytek je na nás.
              </span>
            </h3>
            <p className="mt-4 text-slate-300">
              Ozveme se zpravidla do hodiny v pracovní době. Vše vyřídíme za vás.
            </p>

            <a
              href={`tel:${PHONE.replace(/\s+/g, "")}`}
              data-testid="contact-call-btn"
              className="mt-8 inline-flex items-center gap-3 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-6 py-4 font-bold text-slate-900 shadow-[0_18px_40px_-12px_rgba(212,175,55,0.55)] transition-all hover:-translate-y-0.5"
            >
              <Phone className="h-5 w-5" />
              Zavolat Lence — {PHONE}
            </a>
          </div>

          <div className="lg:col-span-7">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="rounded-3xl border border-amber-300/15 bg-white/[0.03] p-7 lg:p-10 backdrop-blur-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Jméno a příjmení *" name="name" value={form.name} onChange={handleChange} placeholder="Jan Novák" testid="contact-input-name" required />
                <Field label="Telefon *" name="phone" value={form.phone} onChange={handleChange} placeholder="+420 ___ ___ ___" testid="contact-input-phone" type="tel" required />
                <Field label="Email" name="email" value={form.email} onChange={handleChange} placeholder="jan@email.cz" testid="contact-input-email" type="email" className="sm:col-span-2" />

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-amber-300/80 mb-2">
                    Kategorie služby *
                  </label>
                  <select
                    name="service_category"
                    value={form.service_category}
                    onChange={handleChange}
                    data-testid="contact-select-category"
                    required
                    className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none transition"
                  >
                    {PRICING_CATEGORIES.map((c) => (
                      <option key={c.key} value={c.label}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <Field label="Konkrétní služba" name="service" value={form.service} onChange={handleChange} placeholder="např. STK + emise – osobní vůz" testid="contact-input-service" />
                <Field label="Vozidlo (značka, model, RZ)" name="vehicle" value={form.vehicle} onChange={handleChange} placeholder="Škoda Octavia, 1AB 2345" testid="contact-input-vehicle" className="sm:col-span-2" />

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-amber-300/80 mb-2">
                    Vaše zpráva
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    data-testid="contact-input-message"
                    placeholder="Krátká poznámka, preferovaný termín, dotaz..."
                    rows={4}
                    className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none transition resize-y"
                  />
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-amber-300/15 bg-white/[0.03] p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="gdpr"
                    checked={form.gdpr}
                    onChange={handleChange}
                    data-testid="contact-gdpr-checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-amber-400"
                  />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    Souhlasím se zpracováním osobních údajů za účelem vyřízení mé poptávky podle{" "}
                    <Link to="/ochrana-osobnich-udaju" className="text-amber-300 underline underline-offset-2 hover:text-amber-200" target="_blank" rel="noopener noreferrer">
                      Zásad ochrany osobních údajů
                    </Link>
                    .
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit-btn"
                className="mt-5 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 disabled:opacity-70 disabled:cursor-not-allowed px-7 py-4 font-bold text-slate-900 transition-all"
              >
                {status === "loading" ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Odesílám…</>
                ) : (<>Odeslat objednávku</>)}
              </button>

              {status === "success" && (
                <div data-testid="contact-success" className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-emerald-200 text-sm">
                  <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                  <span>Děkujeme! Vaše poptávka byla odeslána. Ozveme se telefonicky zpravidla do hodiny.</span>
                </div>
              )}

              {status === "error" && errorMsg && (
                <div data-testid="contact-error" className="mt-5 flex items-start gap-3 rounded-xl border border-rose-400/30 bg-rose-400/10 p-4 text-rose-200 text-sm">
                  <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                Vaše údaje použijeme pouze pro vyřízení poptávky a následnou komunikaci ohledně požadované služby. Údaje nesdílíme s třetími stranami nad rámec nezbytný pro vyřízení služby.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
});

function InfoTile({ icon, label, sub, href, testid, accent }) {
  const inner = (
    <div className={[
      "rounded-2xl border p-5 transition-all h-full",
      accent
        ? "border-amber-300/40 bg-amber-300/10 hover:bg-amber-300/15"
        : "border-amber-300/15 bg-white/[0.03] hover:border-amber-300/30",
    ].join(" ")}>
      <div className={[
        "flex h-11 w-11 items-center justify-center rounded-xl",
        accent ? "bg-amber-300 text-slate-900" : "bg-amber-300/10 text-amber-300 border border-amber-300/20"
      ].join(" ")}>
        {icon}
      </div>
      <div className={["font-heading mt-4 text-base font-bold", accent ? "text-amber-200" : "text-white"].join(" ")}>
        {label}
      </div>
      <div className="mt-1 text-xs text-slate-400">{sub}</div>
    </div>
  );
  if (href) return <a href={href} data-testid={testid}>{inner}</a>;
  return inner;
}

function Field({ label, name, value, onChange, placeholder, testid, type = "text", required, className }) {
  return (
    <div className={className || ""}>
      <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-amber-300/80 mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none transition"
      />
    </div>
  );
}

export default Contact;
